"use client";

import {
  Account,
  Card,
  refreshAccount,
  useAccount,
  useAuthCall,
  useAuthCallFunction,
} from "@web/lib/auth";
import { toTitleCase } from "@web/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import BillingLink from "./BillingLink";
import { Loader } from "./Loader";
import StripeAddPayment from "./Stripe";

function useUpdateAccount(
  details: Partial<Account & { oldPassword: string; newPassword: string }>,
): [() => Promise<void>, boolean] {
  const call = useAuthCall("PATCH", "account/update", details);
  const [busy, setBusy] = useState(false);
  const callback = useCallback(async () => {
    setBusy(true);
    try {
      await call();
    } finally {
      setBusy(false);
      refreshAccount();
    }
  }, [call]);
  return [callback, busy];
}
function useFocus<T>(): [React.RefObject<T>, () => void] {
  const htmlElRef = useRef<T>(null);
  const setFocus = () => {
    htmlElRef.current && (htmlElRef.current as any).focus();
  };

  return [htmlElRef, setFocus];
}

function PasswordChangeSection() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [updateAccount, busy] = useUpdateAccount({
    oldPassword,
    newPassword,
  });
  return (
    <>
      <div className="text-xl font-semibold mb-4">Change Password</div>
      <div className="">
        <div className="text-lg mb-6">
          <div className="pb-3">Old password</div>
          <input
            placeholder="Enter your currnet password"
            type="password"
            value={oldPassword}
            className="w-full sm:w-96 focus:outline border-[#C0C0C0] bg-[#F7F7F7] border px-4 py-2 rounded-xl text-base font-semibold"
            onChange={(e) => setOldPassword(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="text-lg mb-6">
          <div className="pb-3">New password</div>
          <input
            placeholder="Enter your new password"
            type="password"
            value={newPassword}
            className="w-full sm:w-96 focus:outline border-[#C0C0C0] bg-[#F7F7F7] border px-4 py-2 rounded-xl text-base font-semibold"
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <button
        disabled={busy}
        onClick={updateAccount}
        className="text-lg bg-[#121212] rounded-lg text-white py-3 px-10 hover:opacity-90 disabled:opacity-90 focus:outline"
      >
        Change
      </button>
    </>
  );
}

function EditableText({
  text,
  onChange,
  className,
  onUpdate,
  busy,
}: {
  className?: string;
  text?: string;
  busy?: boolean;
  onChange: (text: string) => void;
  onUpdate: () => void;
}) {
  const [editable, setEditable] = useState(false);
  const [inputRef, setInputFocus] = useFocus<HTMLInputElement>();

  useEffect(() => {
    if (editable) {
      setInputFocus();
    }
  }, [editable]);

  const submit = () => {
    setEditable(false);
    onUpdate();
  };

  let renderText =
    (text?.length ?? 0 > 0) ? (
      text
    ) : (
      <span className="text-white border"> </span>
    );

  return (
    <span
      className={`relative min-w-[1em] w-min ${busy ? "text-gray-500" : ""}`}
    >
      <span
        onClick={() => {
          setEditable(true);
        }}
        className={
          `${editable ? "invisible" : ""} cursor-pointer whitespace-pre ` +
          (className ?? "")
        }
      >
        {renderText}
      </span>
      <input
        ref={inputRef}
        onBlur={submit}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === "Escape") submit();
        }}
        className={
          `${
            editable ? "" : "hidden"
          } bg-transparent outline-none border mt-[-1px] rounded left-0 absolute w-full ` +
          (className ?? "")
        }
        type="text"
        value={text}
        onChange={(e) => onChange(e.target.value)}
      >
        {}
      </input>
    </span>
  );
}

function EditProfile() {
  const profile = useAccount();
  const profileCards = Array.isArray(profile?.cards)
    ? (profile?.cards ?? [])
    : [];
  const [email, setEmail] = useState(profile?.email);
  const [editEmail, setEditEmail] = useState(false);
  const [firstName, setFirstName] = useState(profile?.firstName);
  const [lastName, setLastName] = useState(profile?.lastName);

  const [updateEmail, emailBusy] = useUpdateAccount({
    email: email,
  });

  const [updateFirstName, firstNameBusy] = useUpdateAccount({
    firstName: firstName,
  });

  const [updateLastName, lastNameBusy] = useUpdateAccount({
    lastName: lastName,
  });

  useEffect(() => {
    setFirstName(profile?.firstName);
  }, [profile?.firstName]);

  useEffect(() => {
    setLastName(profile?.lastName);
  }, [profile?.lastName]);

  useEffect(() => {
    setEmail(profile?.email);
  }, [profile?.email]);

  const saveEmail = () => {
    if (!editEmail) {
      setEditEmail(true);
    } else {
      setEditEmail(false);
      updateEmail();
    }
  };
  if (!profile) {
    return <Loader resource="profile" />;
  }

  return (
    <>
      <div className="flex pt-4">
        <div className="text-lg pr-2">Name:</div>
        <div className="pb-1 ">
          <EditableText
            onUpdate={updateFirstName}
            className="text-lg text-gray-500"
            busy={firstNameBusy}
            text={firstName}
            onChange={setFirstName}
          />{" "}
          <EditableText
            onUpdate={updateLastName}
            className="text-lg text-gray-500"
            busy={lastNameBusy}
            text={lastName}
            onChange={setLastName}
          />
        </div>
      </div>
      <div className="flex pt-4">
        <div className="text-lg pr-2">Email:</div>
        <div className="pb-1 ">
          <EditableText
            onUpdate={saveEmail}
            className="text-lg text-gray-500"
            busy={emailBusy}
            text={email}
            onChange={setEmail}
          />
        </div>
      </div>

      <div className="w-full border-t my-10" />
      <div className="pt-1 pb-1">
        <div className="text-xl font-semibold mb-4">Payment Methods</div>
        {...profileCards.map((card) => (
          <CardDisplay key={card.id} card={card} />
        ))}
        <div className="pt-2" />
        <StripeAddPayment />
      </div>
      <div className="w-full border-t my-10" />
      <div className="mt-4 mb-14">
        <PasswordChangeSection />
      </div>
    </>
  );
}

function CardDisplay({ card }: { card: Card }) {
  const authCall = useAuthCallFunction();
  return (
    <div className="flex text-sm justify-between items-center">
      <div>
        {toTitleCase(card.type)} {card.last4}
      </div>
      <div>
        {card.isDefault ? (
          <button className="text-[#121212] font-semibold py-1 px-3">
            Default
          </button>
        ) : (
          <button
            className="bg-[#121212] rounded-lg text-white py-1 px-3 hover:opacity-90 disabled:opacity-20 focus:outline"
            onClick={async () => {
              await authCall("POST", `payment/card/${card.id}/default`);
              refreshAccount();
            }}
          >
            Set Default
          </button>
        )}
        <button
          className="inline-flex ml-2 justify-center items-center text-lg bg-red-700 rounded-lg text-white py-1 px-3 hover:opacity-90 disabled:opacity-20 focus:outline"
          onClick={async () => {
            await authCall("DELETE", `payment/card/${card.id}`);
            refreshAccount();
          }}
        >
          x
        </button>
      </div>
    </div>
  );
}

export function UserMenu() {
  const account = useAccount();
  const accountCards = Array.isArray(account?.cards)
    ? (account?.cards ?? [])
    : [];
  // const { display, ...controls } = useModalShow();

  return (
    <>
      {accountCards.length === 0 ? (
        <BillingLink
          // href="/dashboard/billing"
          // onClick={display}
          className="w-full hover:opacity-80 justify-center flex flex-col h-[86px] px-[28px] rounded-lg border-[0.5px] border-white text-white text-base leading-tight"
        >
          <div className="font-bold">No payment method attached</div>
          <div className="text-xs pt-1">
            Automatic subscription payment might fail if your account balance
            isn't sufficient.
          </div>
        </BillingLink>
      ) : (
        <></>
      )}

      {/* <AutoModal {...controls} title="Profile">
        <EditProfile />
      </AutoModal> */}
    </>
  );
}
